import { adminFetch } from "./_client";
import type { NewsletterSubscribersResponse, NewsletterStats, ConciergeLeadStatus, ConciergeLead, ConciergeLeadsResponse, ConciergeLeadsQuery, HistoryLog, ImageUploadTestResult } from "./types";


export function fetchNewsletterSubscribers(token: string, page = 1, status?: string, search?: string, dateFrom?: string, dateTo?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (status && status !== "all") params.set("status", status);
  if (search) params.set("search", search);
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  return adminFetch<NewsletterSubscribersResponse>(`/admin/newsletter/subscribers?${params}`, token);
}


export function exportNewsletterSubscribers(token: string, status?: string) {
  const params = new URLSearchParams();
  if (status && status !== "all") params.set("status", status);
  const url = `${import.meta.env.BASE_URL || "/"}api/admin/newsletter/subscribers/export?${params}`;
  return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => {
    if (!r.ok) throw new Error("Export failed");
    return r.blob();
  });
}


export function addNewsletterSubscriber(token: string, data: { email: string; first_name?: string; last_name?: string; source?: string }) {
  return adminFetch<{ success: boolean; resubscribed?: boolean }>("/admin/newsletter/subscribers", token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}


export function importNewsletterSubscribers(token: string, subscribers: { email: string; first_name?: string; last_name?: string }[]) {
  return adminFetch<{ success: boolean; imported: number; skipped: number; errors: number }>("/admin/newsletter/subscribers/import", token, {
    method: "POST",
    body: JSON.stringify({ subscribers }),
  });
}


export function unsubscribeNewsletter(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/newsletter/subscribers/${id}/unsubscribe`, token, { method: "POST" });
}


export function resubscribeNewsletter(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/newsletter/subscribers/${id}/resubscribe`, token, { method: "POST" });
}


export function fetchNewsletterStats(token: string) {
  return adminFetch<NewsletterStats>("/admin/newsletter/stats", token);
}


export function fetchConciergeLeads(token: string, params: ConciergeLeadsQuery = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.search) qs.set("search", params.search);
  if (params.status) qs.set("status", params.status);
  if (params.source) qs.set("source", params.source);
  if (params.intercom_delivered) qs.set("intercom_delivered", params.intercom_delivered);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.sort) qs.set("sort", params.sort);
  return adminFetch<ConciergeLeadsResponse>(
    `/admin/concierge-leads${qs.toString() ? `?${qs.toString()}` : ""}`,
    token,
  );
}


export function fetchConciergeLead(token: string, id: number) {
  return adminFetch<{ lead: ConciergeLead }>(`/admin/concierge-leads/${id}`, token);
}


export function updateConciergeLead(
  token: string,
  id: number,
  updates: { status?: ConciergeLeadStatus; admin_notes?: string | null },
) {
  return adminFetch<{ lead: ConciergeLead }>(`/admin/concierge-leads/${id}`, token, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}


export function previewEmailTemplate(token: string, template: string, sampleData?: Record<string, string>) {
  return adminFetch<{ html: string; template: string; data: Record<string, string> }>("/admin/tools/email-preview", token, {
    method: "POST",
    body: JSON.stringify({ template, sampleData }),
  });
}


export function fetchHistoryLogs(token: string, params: {
  page?: number;
  entity_type?: string;
  action?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.entity_type) qs.set("entity_type", params.entity_type);
  if (params.action) qs.set("action", params.action);
  if (params.user_id) qs.set("user_id", params.user_id);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.search) qs.set("search", params.search);
  return adminFetch<{ logs: HistoryLog[]; total: number; page: number; limit: number; entityTypes: string[] }>(`/admin/tools/history-logs?${qs}`, token);
}


export function runImageUploadTest(token: string) {
  return adminFetch<{ results: ImageUploadTestResult[]; overallStatus: string }>("/admin/tools/image-upload-test", token, { method: "POST" });
}
